/**
 * AI Usage Stats Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Usage dashboard with statistics and charts.
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Shield,
  AlertTriangle,
  Eye,
  Clock,
  RefreshCw,
} from 'lucide-react';
import type { AIUsageStats as AIUsageStatsType } from '../../../types/admin';
import { AI_INTERACTION_TYPE_LABELS } from '../../../types/admin';
import { aiSettingsService } from '../../../services/admin';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const PERIOD_OPTIONS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export const AIUsageStats: React.FC = () => {
  const [stats, setStats] = useState<AIUsageStatsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  const loadStats = async (days: number) => {
    setIsLoading(true);
    try {
      const data = await aiSettingsService.getUsageStats(days);
      setStats(data);
    } catch (error) {
      console.error('Failed to load usage stats:', error);
      toast.error('Failed to load usage statistics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStats(selectedPeriod);
  }, [selectedPeriod]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 size={40} className="mx-auto text-gray-300 mb-2" />
        <p>No usage data available</p>
      </div>
    );
  }

  // Calculate max for chart scaling
  const maxInteractions = stats.daily_interactions.length > 0
    ? Math.max(...stats.daily_interactions.map((d) => d.interactions))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Usage Statistics</h3>
          <p className="text-sm text-gray-500 mt-1">
            AI interaction metrics for the selected period
          </p>
        </div>
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.days}
              onClick={() => setSelectedPeriod(option.days)}
              className={`
                px-3 py-1.5 text-sm border rounded-lg transition-colors
                ${selectedPeriod === option.days
                  ? 'bg-[#002B45] text-white border-[#002B45]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }
              `}
            >
              {option.label}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={() => loadStats(selectedPeriod)}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <TrendingUp size={16} />
            <span className="text-xs font-medium">Total Interactions</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{formatNumber(stats.total_interactions)}</p>
        </div>

        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <Clock size={16} />
            <span className="text-xs font-medium">Tokens Used</span>
          </div>
          <p className="text-2xl font-bold text-purple-900">{formatNumber(stats.total_tokens_used)}</p>
        </div>

        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Users size={16} />
            <span className="text-xs font-medium">Unique Users</span>
          </div>
          <p className="text-2xl font-bold text-green-900">{stats.unique_users}</p>
        </div>

        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Shield size={16} />
            <span className="text-xs font-medium">PII Detected</span>
          </div>
          <p className="text-2xl font-bold text-amber-900">{formatNumber(stats.pii_detected_count)}</p>
        </div>
      </div>

      {/* Interactions by Type */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-4">Interactions by Type</h4>
        <div className="space-y-3">
          {Object.entries(stats.interactions_by_type).map(([type, count]) => {
            const percentage = stats.total_interactions > 0
              ? Math.round((count / stats.total_interactions) * 100)
              : 0;
            return (
              <div key={type}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">
                    {AI_INTERACTION_TYPE_LABELS[type as keyof typeof AI_INTERACTION_TYPE_LABELS] || type}
                  </span>
                  <span className="font-medium text-gray-900">{formatNumber(count)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
          {Object.keys(stats.interactions_by_type).length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No interaction data available</p>
          )}
        </div>
      </div>

      {/* Daily Chart */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-4">Daily Interactions</h4>
        {stats.daily_interactions.length > 0 ? (
          <div className="h-40 flex items-end justify-between gap-1">
            {stats.daily_interactions.map((day, index) => {
              const height = maxInteractions > 0 ? (day.interactions / maxInteractions) * 100 : 0;
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-300 rounded-t"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: ${day.interactions} interactions`}
                  />
                  {stats.daily_interactions.length <= 14 && (
                    <span className="text-xs text-gray-400 mt-1 transform -rotate-45 origin-left">
                      {new Date(day.date).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-gray-400">
            <BarChart3 size={40} className="mr-2" />
            No daily data available
          </div>
        )}
      </div>

      {/* Compliance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Eye size={16} />
            <span className="text-sm font-medium">PII Handling</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Detected</span>
              <span className="font-medium">{formatNumber(stats.pii_detected_count)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Masked</span>
              <span className="font-medium">{formatNumber(stats.pii_masked_count)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Blocked in Output</span>
              <span className="font-medium">{formatNumber(stats.pii_blocked_in_output_count)}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Shield size={16} />
            <span className="text-sm font-medium">Compliance</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Compliance Flags</span>
              <span className="font-medium">{formatNumber(stats.compliance_flags_triggered)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Disclaimers Shown</span>
              <span className="font-medium">{formatNumber(stats.disclaimers_shown)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Topics Restricted</span>
              <span className="font-medium">{formatNumber(stats.restricted_topics_detected)}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <AlertTriangle size={16} />
            <span className="text-sm font-medium">Risk Levels</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Standard</span>
              <span className="font-medium text-green-600">
                {formatNumber(
                  stats.total_interactions -
                    stats.elevated_risk_interactions -
                    stats.high_risk_interactions
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Elevated</span>
              <span className="font-medium text-amber-600">
                {formatNumber(stats.elevated_risk_interactions)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">High</span>
              <span className="font-medium text-red-600">
                {formatNumber(stats.high_risk_interactions)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIUsageStats;
